import { cn } from '@localagent/ui/lib/utils';
import { Tooltip } from '@localagent/ui/components/tooltip';

/**
 * A compact mini-map position picker: a row of buttons, each a 24×24 glyph with
 * a filled rect showing where the panel will sit. Extracted from
 * ChatLayoutControls' inline `PositionPicker` so BOTH the chat file panel
 * (4-way: left/right/top/bottom) and the Shogun panel (5-way: + floating) render
 * from one component with identical look-and-feel. Generic over the value type so each consumer keeps its own union.
 *
 * The active option gets `aria-pressed` + a primary-fill highlight; disabled
 * dims the whole group and blocks interaction (a picker that doesn't apply to
 * the current context).
 */
export interface EdgePositionOption<T extends string> {
  value: T;
  /** Accessible name + tooltip, e.g. "Dock Shogun left", "Float Shogun". */
  title: string;
  /** Filled mini-map rect `[x, y, width, height]` in the 24×24 viewBox. */
  rect: [number, number, number, number];
}

export interface EdgePositionPickerProps<T extends string> {
  options: EdgePositionOption<T>[];
  active: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  /** `data-testid` for the group container. */
  testid: string;
  /** Prefix for each option's `data-testid` (`{prefix}-{value}`). */
  optionTestidPrefix: string;
}

export function EdgePositionPicker<T extends string>({
  options,
  active,
  onChange,
  disabled = false,
  testid,
  optionTestidPrefix,
}: EdgePositionPickerProps<T>) {
  return (
    <div className="flex items-center gap-0.5" data-testid={testid}>
      {options.map((o) => (
        <Tooltip key={o.value} label={o.title} side="bottom">
        <button
          type="button"
          title={o.title}
          aria-label={o.title}
          aria-pressed={active === o.value}
          disabled={disabled}
          data-testid={`${optionTestidPrefix}-${o.value}`}
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-sm p-1 transition-colors',
            disabled
              ? 'cursor-not-allowed text-muted-foreground/40'
              : active === o.value
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent',
          )}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="1.5" y="1.5" width="21" height="21" rx="2" />
            <rect
              x={o.rect[0]}
              y={o.rect[1]}
              width={o.rect[2]}
              height={o.rect[3]}
              fill="currentColor"
              stroke="none"
              opacity="0.9"
            />
          </svg>
        </button>
        </Tooltip>
      ))}
    </div>
  );
}
